import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  GraduationCap, 
  ArrowRight, 
  Loader2,
  RefreshCw,
  AlertTriangle,
  Clock,
  User,
  Ban,
  Eye,
  FileText,
  Shield,
  Wifi,
  WifiOff,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const LiveExamMonitor = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { token, isTeacher } = useAuth();
  
  const [examTitle, setExamTitle] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Block dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [sessionToBlock, setSessionToBlock] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchActiveSessions = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/teacher/exams/${examId}/active-sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setExamTitle(data.exam_title);
        setSessions(data.sessions);
        setLastRefresh(new Date());
      } else if (response.status === 404) {
        toast.error('الاختبار غير موجود');
        navigate('/teacher/dashboard');
      }
    } catch (error) {
      console.error('Error fetching active sessions:', error);
    }
    
    setLoading(false);
    setRefreshing(false);
  }, [API_URL, examId, token, navigate]);

  useEffect(() => {
    if (!isTeacher) {
      navigate('/teacher/login');
      return;
    }
    
    fetchActiveSessions();
  }, [isTeacher, navigate, fetchActiveSessions]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchActiveSessions(false);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchActiveSessions]);

  const handleBlockStudent = async () => {
    if (!sessionToBlock) return;
    
    setBlocking(true);
    try {
      const response = await fetch(`${API_URL}/api/teacher/exam-sessions/${sessionToBlock.id}/block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: blockReason || 'تم إيقاف الاختبار من قبل المعلم'
        }),
      });
      
      if (response.ok) {
        toast.success(`تم إيقاف اختبار الطالب ${sessionToBlock.student_name}`);
        fetchActiveSessions(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل إيقاف الاختبار');
      }
    } catch (error) {
      console.error('Error blocking student:', error);
      toast.error('حدث خطأ أثناء إيقاف الاختبار');
    }
    
    setBlocking(false);
    setBlockDialogOpen(false);
    setSessionToBlock(null);
    setBlockReason('');
  };

  const getViolationBadge = (count, max) => {
    if (count === 0) {
      return <Badge className="bg-green-100 text-green-800">بدون مخالفات</Badge>;
    } else if (count >= max) {
      return <Badge variant="destructive">مخالفات: {count}/{max}</Badge>;
    } else if (count >= max - 1) {
      return <Badge className="bg-[#EF9F27]/15 text-[#854F0B]">مخالفات: {count}/{max}</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800">مخالفات: {count}/{max}</Badge>;
  };

  const getViolationTypeLabel = (type) => {
    const labels = {
      'tab_switch': 'تبديل النافذة',
      'focus_loss': 'فقدان التركيز',
      'fullscreen_exit': 'الخروج من ملء الشاشة',
      'screenshot_attempt': 'محاولة لقطة شاشة',
      'screen_record_attempt': 'محاولة تسجيل الشاشة',
      'copy_paste': 'نسخ/لصق',
      'right_click': 'النقر الأيمن'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#6B4C2A]">جاري تحميل بيانات المراقبة...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FDF8F0] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#412402]">المعتمد في اللغة العربية</span>
            </Link>
            <div className="flex items-center gap-2">
              {autoRefresh ? (
                <Badge className="bg-green-100 text-green-800 border border-green-200 gap-1">
                  <Wifi className="w-3 h-3" />
                  تحديث تلقائي
                </Badge>
              ) : (
                <Badge variant="outline" className="border-[#E8DFCF] text-[#6B4C2A] gap-1">
                  <WifiOff className="w-3 h-3" />
                  متوقف
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/teacher/dashboard')}
            className="text-[#6B4C2A] hover:text-[#412402] mb-4"
          >
            <ArrowRight className="w-4 h-4 ml-2" />
            العودة للوحة التحكم
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-[#412402] flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#EF9F27]" />
                مراقبة الامتحان المباشر
              </h1>
              <p className="text-[#6B4C2A] mt-1">{examTitle}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={autoRefresh ? 'text-green-600 border-green-200' : 'text-[#6B4C2A] border-[#E8DFCF]'}
              >
                {autoRefresh ? (
                  <>
                    <Wifi className="w-4 h-4 ml-1" />
                    إيقاف التحديث التلقائي
                  </>
                ) : (
                  <>
                    <WifiOff className="w-4 h-4 ml-1" />
                    تشغيل التحديث التلقائي
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchActiveSessions(true)}
                disabled={refreshing}
                className="border-[#E8DFCF] text-[#854F0B]"
              >
                <RefreshCw className={`w-4 h-4 ml-1 ${refreshing ? 'animate-spin' : ''}`} />
                تحديث
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="text-sm text-[#8B7355]">طلاب نشطون</p>
                <p className="text-2xl font-bold text-[#412402]">{sessions.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#EF9F27]/15 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[#EF9F27]" />
              </div>
              <div>
                <p className="text-sm text-[#8B7355]">إجمالي المخالفات</p>
                <p className="text-2xl font-bold text-[#412402]">
                  {sessions.reduce((sum, s) => sum + s.violation_count, 0)}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FFFFFF] border border-[#E8DFCF] flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#6B4C2A]" />
              </div>
              <div>
                <p className="text-sm text-[#8B7355]">آخر تحديث</p>
                <p className="text-lg font-medium text-[#412402]">
                  {lastRefresh.toLocaleTimeString('ar-SA')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Sessions */}
        <Card className="bg-[#FDF8F0] border border-[#E8DFCF]">
          <CardHeader className="border-b border-[#E8DFCF]">
            <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#EF9F27]" />
              الجلسات النشطة
            </CardTitle>
            <CardDescription className="text-[#8B7355]">
              الطلاب الذين يؤدون الاختبار حالياً
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {sessions.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-[#E8DFCF] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#6B4C2A] mb-2">لا توجد جلسات نشطة</h3>
                <p className="text-[#8B7355]">لا يوجد طلاب يؤدون الاختبار حالياً</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E8DFCF]">
                {sessions.map((session) => (
                  <div 
                    key={session.id} 
                    className="p-4 hover:bg-[#FFFFFF] transition-colors"
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Student Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-[#412402]">{session.student_name}</h3>
                          {getViolationBadge(session.violation_count, session.max_violations)}
                          {session.last_violation_type && (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                              آخر مخالفة: {getViolationTypeLabel(session.last_violation_type)}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B4C2A]">
                          <span>{session.student_grade} / {session.student_section}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            منذ {session.duration_minutes} دقيقة
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            أجاب على {session.answers_count}/{session.total_questions}
                          </span>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full md:w-32">
                        <div className="h-2 bg-[#E8DFCF] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#EF9F27] transition-all"
                            style={{ width: `${(session.answers_count / session.total_questions) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#8B7355] mt-1 text-center">
                          {Math.round((session.answers_count / session.total_questions) * 100)}% مكتمل
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={() => {
                            setSessionToBlock(session);
                            setBlockDialogOpen(true);
                          }}
                          data-testid={`block-btn-${session.id}`}
                        >
                          <Ban className="w-4 h-4 ml-1" />
                          إيقاف
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Block Confirmation Dialog */}
      <AlertDialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <AlertDialogContent className="bg-[#FDF8F0] border border-[#E8DFCF]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              إيقاف اختبار الطالب
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B4C2A]">
              هل أنت متأكد من إيقاف اختبار الطالب <strong>{sessionToBlock?.student_name}</strong>؟
              <br />
              سيتم إنهاء الاختبار فوراً وحساب درجته بناءً على إجاباته الحالية.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="سبب الإيقاف (اختياري)..."
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              rows={3}
              className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402]"
            />
          </div>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel 
              onClick={() => {
                setBlockReason('');
                setSessionToBlock(null);
              }}
              className="border-[#E8DFCF] text-[#412402]"
            >
              إلغاء
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBlockStudent}
              className="bg-red-600 hover:bg-red-700"
              disabled={blocking}
            >
              {blocking ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  جاري الإيقاف...
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4 ml-2" />
                  إيقاف الاختبار
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LiveExamMonitor;
